import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "id"];
import styled, { css, ThemeProvider } from 'styled-components';
import { useLocation } from 'react-router-dom';
import useLocale from 'developer-docs-shared-components/hooks/useLocale';
import MobileScrollToTop from './MobileScrollToTop';
import { mediaQueries, MOBILE_MENU_BREAKPOINT } from 'developer-docs-shared-components/utils/mediaQueries';
import I18n from 'I18n';
import Header from './Header';
import themes from 'developer-docs-shared-components/config/themes';
import { ViewportProvider } from 'developer-docs-shared-components/context/ViewportContext';
import ResponsiveRenderer from 'developer-docs-shared-components/components/ResponsiveRenderer';
import Footer from './Footer';
import { MAIN_CONTENT_ID } from '../config/accessibility';
import UILink from 'UIComponents/link/UILink';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { TOC_RIGHT_OFFSET, TOC_WIDTH, TOC_WIDTH_NARROW } from './toc/constants';
import GDPRCompliance from './GDPRCompliance';
import DocFeedback from './DocFeedback';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ViewportHeightWrapper = styled.div.withConfig({
  displayName: "LayoutPublic__ViewportHeightWrapper",
  componentId: "bg43lv-0"
})(["height:100vh;display:flex;flex-direction:column;"]);

/**
 * Divides the secondary nav and main contents into two columns
 * (secondary nav and scroll wrapper). A sibling after the header in
 * the DOM tree.
 */
const Main = styled.main.withConfig({
  displayName: "LayoutPublic__Main",
  componentId: "bg43lv-1"
})(["padding:14px 16px;", "{display:flex;justify-content:space-between;overflow-y:hidden;height:100%;padding:0;flex-direction:row-reverse;}", ""], mediaQueries.aboveWidth(MOBILE_MENU_BREAKPOINT), ({
  mainStyles
}) => mainStyles ? `${mainStyles}` : null);

/**
 * The scrollable area of this page layout. Houses the main contents
 * (article and supplemental TOC nav) and footer. Targeted for scroll
 * events in the TOC.
 */
const ScrollContainer = styled(_ref => {
  let {
      children,
      id
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsxs("div", Object.assign({}, rest, {
    id: id,
    children: [children, /*#__PURE__*/_jsx(MobileScrollToTop, {
      href: `#${id}`
    })]
  }));
}).withConfig({
  displayName: "LayoutPublic__ScrollContainer",
  componentId: "bg43lv-2"
})(["flex:1;", "{position:relative;}", "{overflow-y:auto;}"], mediaQueries.belowWidth(MOBILE_MENU_BREAKPOINT), mediaQueries.aboveWidth(MOBILE_MENU_BREAKPOINT));
const StickyInnerWrapper = styled.div.withConfig({
  displayName: "LayoutPublic__StickyInnerWrapper",
  componentId: "bg43lv-3"
})(["", "{position:sticky;top:0;right:0;}"], mediaQueries.aboveWidth(MOBILE_MENU_BREAKPOINT));

/**
 * Used on layouts with a table of contents.
 */
export const StickyWrapper = styled(props => /*#__PURE__*/_jsx(StickyInnerWrapper, {
  children: /*#__PURE__*/_jsx("nav", Object.assign({}, props))
})).withConfig({
  displayName: "LayoutPublic__StickyWrapper",
  componentId: "bg43lv-4"
})(["", "{position:absolute;top:0;width:", "px;right:0;mask-image:linear-gradient(180deg,#000 95%,transparent);max-height:calc(100vh - 86px);overflow-y:auto;padding-bottom:32px;&::-webkit-scrollbar{display:none;}}", "{right:", "px;width:", "px;}"], mediaQueries.aboveWidth(MOBILE_MENU_BREAKPOINT), TOC_WIDTH_NARROW, mediaQueries.aboveLaptop, TOC_RIGHT_OFFSET, TOC_WIDTH);
const MaxWidthWrapper = styled.div.withConfig({
  displayName: "LayoutPublic__MaxWidthWrapper",
  componentId: "bg43lv-5"
})(["max-width:", ";margin:0 auto;", "{", " ", "}"], ({
  maxContentWidth
}) => maxContentWidth ? `${maxContentWidth}px` : null, mediaQueries.aboveWidth(MOBILE_MENU_BREAKPOINT), ({
  sideNavComponent
}) => sideNavComponent ? 'padding-left: 32px;' : 'padding: 14px 16px 0 16px;', ({
  maxContentWidth
}) => maxContentWidth === null ? css(["padding-left:0;padding-right:0;"]) : null);
export const SCROLL_CONTAINER_ID = 'top';

/**
 * A shared layout for all public docs pages. Automatically handles accessible
 * 'skip to main content' and nav markers for you.
 */
const LayoutPublic = ({
  children,
  FooterComponent = Footer,
  footerWidth = 'article',
  hasClearedPassword = true,
  isSearchResultsPage,
  mainStyles,
  maxContentWidth,
  mdxDocPageId = 29866608851,
  sideNavComponent,
  showFeedbackForm = true
}) => {
  const {
    pathname
  } = useLocation();
  const locale = useLocale();
  return /*#__PURE__*/_jsxs(ThemeProvider, {
    theme: themes.PUBLIC,
    children: [/*#__PURE__*/_jsx(GDPRCompliance, {
      pathname: pathname
    }), /*#__PURE__*/_jsx(ViewportProvider, {
      queries: {
        isMobile: `(max-width: ${themes.PUBLIC.mobileBreakpoint - 1}px)`,
        isDesktop: `(min-width: ${themes.PUBLIC.mobileBreakpoint}px)`,
        isBelowLaptop: `(max-width: ${themes.PUBLIC.belowLaptopBreakpoint}px)`,
        isAboveColumn: `(min-width: ${themes.PUBLIC.aboveColumnBreakpoint}px)`,
        isBetweenMobileAndColumn: `(min-width: ${themes.PUBLIC.mobileBreakpoint}px) and (max-width: ${themes.PUBLIC.aboveColumnBreakpoint - 1}px)`,
        isBelowTablet: `(max-width: ${themes.PUBLIC.belowTabletBreakpoint}px)`
      },
      children: /*#__PURE__*/_jsxs(ViewportHeightWrapper, {
        "data-test-id": `LOCALE_${locale.toUpperCase()}`,
        children: [/*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "developer-docs-ui.accessibility.skipLinkText_jsx",
          options: {
            href: `#${MAIN_CONTENT_ID}`,
            className: 'sr-only sr-only-focusable'
          },
          elements: {
            Link: UILink
          }
        }), /*#__PURE__*/_jsx(Header, {
          isSearchResultsPage: isSearchResultsPage
        }), /*#__PURE__*/_jsxs(Main, {
          id: MAIN_CONTENT_ID,
          mainStyles: mainStyles,
          children: [/*#__PURE__*/_jsx(ScrollContainer, {
            id: SCROLL_CONTAINER_ID,
            children: /*#__PURE__*/_jsxs(MaxWidthWrapper, {
              maxContentWidth: maxContentWidth,
              sideNavComponent: sideNavComponent,
              children: [children, hasClearedPassword && /*#__PURE__*/_jsxs(_Fragment, {
                children: [showFeedbackForm && /*#__PURE__*/_jsx(DocFeedback, {
                  title: I18n.text('developer-docs-ui.htmlTitle'),
                  pageId: mdxDocPageId
                }), FooterComponent === Footer ? /*#__PURE__*/_jsx(Footer, {
                  width: footerWidth
                }) : /*#__PURE__*/_jsx(FooterComponent, {})]
              })]
            })
          }), /*#__PURE__*/_jsx(ResponsiveRenderer, {
            renderDesktop: () => sideNavComponent,
            renderMobile: () => null
          })]
        })]
      })
    })]
  });
};
export default LayoutPublic;